import { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import * as Lodash from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { DragAndDropFuncs } from '../../../../common/core/drag-and-drop/drag-and-drop.funcs';
import { Button } from '../../../../common/view/button/button/button';
import { Color } from '../../../../common/view/color/color';
import { MonoIcon } from '../../../../common/view/icon/mono/mono-icon';
import { DndKit } from '../../../../external/dnd-kit/dnd-kit';
import { NodeTypeWire } from '../../../../external/server/node-type.wire';
import { ServerApi } from '../../../../external/server/server.api';
import { ArrayFuncs } from '../../../../foundation/js/array.funcs';
import { JsFuncs } from '../../../../foundation/js/js.funcs';
import { TripleX } from '../../../../foundation/layout/triple-x';
import { MegaBoardContentWireFuncs } from '../../../core/board/mega/mega-board-content.wire.funcs';
import { BasicNodeSubWire } from '../../../core/node/basic-node-sub.wire';
import { NodeStore } from '../../../core/node/node.store';
import { NodeWireFuncs } from '../../../core/node/node.wire.funcs';
import { AuthStateContext } from '../../../core/user/auth.context';
import { AppStore } from '../app.store';
import { CentralDragAndDropStore } from '../drag-and-drop/central-drag-and-drop.store';
import { SideBarItem } from './side-bar-item';
import { SideBarStyle } from './side-bar.style';

function SideBarBoardItem({ boardId, selected }: { boardId: string; selected: boolean }) {
  const board = NodeStore.useItem(boardId);
  const sortable = useSortable({ id: board.id, data: { type: 'side-bar-board' } });

  const handleClick = useCallback(() => {
    AppStore.update({ currentBoardId: boardId });
  }, [boardId]);

  return (
    <div
      ref={sortable.setNodeRef}
      {...sortable.listeners}
      {...sortable.attributes}
      style={{ transform: DndKit.CSS.Transform.toString(sortable.transform), transition: sortable.transition }}
    >
      <SideBarItem text={board?.text} icon={MonoIcon.megaboard} selected={selected} onClick={handleClick} />
    </div>
  );
}

export function SideBarBoardSection() {
  const authState = AuthStateContext.use();
  const appState = AppStore.use();
  const [rootNodeId, setRootNodeId] = useState<string>();
  const rootNode = NodeStore.useItem(rootNodeId);

  useEffect(() => {
    (async () => {
      if (authState.state.isAuthenticated) {
        const rootNode = await ServerApi.getRootNode();

        const nodes = await ServerApi.getNodes(ArrayFuncs.ensure(rootNode.sub as BasicNodeSubWire));
        NodeStore.updateItems(Lodash.keyBy(nodes, 'id'));

        NodeStore.updateItem(rootNode.id, rootNode);
        setRootNodeId(rootNode.id);

        // set the first board as the current board
        if (rootNode.sub && rootNode.sub.length > 0) {
          const firstBoardId = rootNode.sub[0];
          AppStore.update({ currentBoardId: firstBoardId });
        }
      }
    })();
  }, [authState.state.isAuthenticated]);

  const handleCreateBoard = useCallback(async () => {
    // local change
    const newBoard = NodeWireFuncs.init({
      text: 'New Board',
      type: NodeTypeWire.mega,
      sub: MegaBoardContentWireFuncs.starter(),
    });
    NodeStore.updateItem(newBoard.id, newBoard);

    const sub = ArrayFuncs.ensure(rootNode.sub as BasicNodeSubWire);
    sub.push(newBoard.id);
    NodeStore.updateItem(rootNode.id, { sub });

    // remote change
    const createdBoard = await ServerApi.createNode(newBoard);
    NodeStore.updateItem(createdBoard.id, createdBoard);

    await ServerApi.updateNode(rootNode.id, { sub });
  }, [rootNode]);

  useEffect(() => {
    function handleDragEnd(e: DragEndEvent) {
      if (JsFuncs.isNull(e.over)) {
        return;
      }

      const startingId = e.active.id as string;
      const endingId = e.over?.id as string;

      if (startingId === endingId) {
        return;
      }

      const sub = ArrayFuncs.ensure(rootNode.sub as BasicNodeSubWire);
      const newSub = DragAndDropFuncs.moveItemsInArray({ items: sub, starting: startingId, ending: endingId });

      NodeStore.updateItem(rootNode.id, { sub: newSub });
      ServerApi.updateNode(rootNode.id, { sub: newSub });
    }

    CentralDragAndDropStore.register({ type: 'side-bar-board', handler: { onDragEnd: handleDragEnd } });
  }, [rootNode]);

  return (
    <SideBarStyle.Section>
      <TripleX.Container $align="center" style={{ padding: '2px 0px 2px 10px' }}>
        <TripleX.Rest>
          <SideBarStyle.SectionTitle>Boards</SideBarStyle.SectionTitle>
        </TripleX.Rest>
        <TripleX.Side>
          <Button icon={MonoIcon.plus} color={Color.white7} onClick={handleCreateBoard} />
        </TripleX.Side>
      </TripleX.Container>
      <SortableContext items={ArrayFuncs.ensure(rootNode?.sub)}>
        {ArrayFuncs.ensure(rootNode?.sub)
          .filter((e) => e)
          .filter((boardId) => NodeStore.getItem(boardId))
          .map((boardId) => (
            <SideBarBoardItem key={boardId} boardId={boardId} selected={appState.currentBoardId === boardId} />
          ))}
      </SortableContext>
    </SideBarStyle.Section>
  );
}
